import * as Yup from 'yup'

export const loginSchema = Yup.object().shape({
  email: Yup.string()
    .email('Invalid email address')
    .required('Please enter your email address'),
  password: Yup.string().required('Please enter your password'),
})

export const signupSchema = Yup.object().shape({
  first_name: Yup.string().required('Please enter your first name'),
  last_name: Yup.string().required('Please enter your last name'),
  email: Yup.string()
    .email('Invalid email address')
    .required('Please enter your email address'),
  password: Yup.string()
    .transform((value) => value.trim())
    .required('Please enter your password')
    .matches(
      /^(?=.*[A-Z])(?=.*[a-z])(?=.*[0-9])(?=.*[\W_]).{8,}$/,
      'The password must include at least one uppercase letter, one lowercase letter, one digit, one special character, and be a minimum of 8 characters long.',
    ),
  phone: Yup.string()
    .required('Please enter your phone number')
    .matches(
      /^(\d{10}|\(\d{3}\) \d{3}-\d{4})$/,
      'Phone number must be in the format (xxx) xxx-xxxx',
    ),
  termsAccepted: Yup.boolean().oneOf(
    [true],
    'You must accept Terms & Conditions',
  ),
})

export const forgotPasswordRequestSchema = Yup.object().shape({
  email: Yup.string()
    .email('Invalid email')
    .required('Please enter your Email'),
})

export const resetPasswordSchema = Yup.object().shape({
  password: Yup.string()
    .transform((value) => value.trim())
    .required('Please enter your Password')
    .matches(
      /^(?=.*[A-Z])(?=.*[a-z])(?=.*[0-9])(?=.*[\W_]).{8,}$/,
      'Password must contain at least one uppercase letter, one lowercase letter, one digit, one special character, and be at least 8 characters long',
    ),
  confirmpassword: Yup.string()
    .transform((value) => value.trim())
    .required('Please enter your confirm password')
    .oneOf(
      [Yup.ref('password'), null],
      'Password and confirm password are not the same',
    ),
})

export const vinDecoderSchema = Yup.object().shape({
  vin_number: Yup.string()
    .matches(/^[a-zA-Z0-9]+$/, 'Only alphanumeric characters are allowed')
    .required('VIN is required.')
    .max(17, 'Must be exactly 17 digits')
    .min(17, 'Must be exactly 17 digits')
    .test('trim', 'Please provide valid vin', (value) => {
      return value.trim().length === value.length
    }),
})

export const vehicleDetailsSchema = Yup.object().shape({
  odometer: Yup.string().required('Please enter odometer'),
  mileage_unit: Yup.string().required('Please select mileage unit'),
  interior_color: Yup.string().required('Please select interior color'),
  exterior_color: Yup.string().required('Please select exterior color'),
  // has_accident: Yup.string().required('Please select accident information'),
})

export const vehicleImagesSchema = Yup.object().shape({
  exterior_main: Yup.string().required('Exterior image is required'),
  interior_driver: Yup.string().required('Interior image is required'),
})

export const registrationProcessPersonalDetailsSchema = Yup.object().shape({
  first_name: Yup.string().required('Please enter your name'),
  last_name: Yup.string().required('Please enter your last name'),
  email: Yup.string()
    .email('Invalid email address')
    .required('Please enter your email address'),
  phone: Yup.string()
    .required('Phone number is required')
    .matches(
      /^(\d{10}|\(\d{3}\) \d{3}-\d{4})$/,
      'Phone number must be in the format (xxx) xxx-xxxx',
    ),
})

export const sellerRegistrationProcessValidationSchema = (isSharingInventory) =>
  Yup.object().shape({
    name: Yup.string().required('Dealership name is required'),
    phone: Yup.string()
      .required('Phone number is required')
      .matches(
        /^(\d{10}|\(\d{3}\) \d{3}-\d{4})$/,
        'Phone number must be in the format (xxx) xxx-xxxx',
      ),
    contact_person: Yup.string().required('Contact person is required'),
    email: Yup.string().email('Invalid email address'),
    website: Yup.string().matches(
      /((https?):\/\/)?(www.)?[a-z0-9]+(\.[a-z]{2,}){1,3}(#?\/?[a-zA-Z0-9#]+)*\/?(\?[a-zA-Z0-9-_]+=[a-zA-Z0-9-%]+&?)?$/,
      'Enter a valid URL',
    ),
    dealer_code: Yup.string().when([], {
      is: () => isSharingInventory,
      then: (schema) =>
        schema.required(
          'Dealer code is required if you want to sharing inventory',
        ),
      otherwise: (schema) => schema.notRequired(),
    }),
    address: Yup.array().of(
      Yup.object().shape({
        street: Yup.string().required('Street is required'),
        city: Yup.string().required('City is required'),
        province: Yup.string().required('Country is required'),
        postcode: Yup.string().required('Postcode is required'),
        country: Yup.string().required('Please select country'),
      }),
    ),
    hst_number: Yup.string().when('address', (address, schema) => {
      const hasCanada = address?.[0]?.some
        ? address[0].some((addr) => addr.country === 'Canada')
        : false

      return hasCanada
        ? schema.required('HST Number is required for Canadian dealers')
        : schema.notRequired()
    }),
  })

export const buyerRegistrationProcessValidationSchema = (isSharingInventory) =>
  Yup.object().shape({
    name: Yup.string().required('Dealer name is required'),
    phone: Yup.string()
      .required('Phone number is required')
      .matches(
        /^(\d{10}|\(\d{3}\) \d{3}-\d{4})$/,
        'Phone number must be in the format (xxx) xxx-xxxx',
      ),
    contact_person: Yup.string().required('Contact person is required'),
    intrested_province: Yup.array().min(
      1,
      'Please select at least one state/province from where you wish to purchase a vehicle.',
    ),
    email: Yup.string().email('Invalid email address'),
    website: Yup.string().matches(
      /((https?):\/\/)?(www.)?[a-z0-9]+(\.[a-z]{2,}){1,3}(#?\/?[a-zA-Z0-9#]+)*\/?(\?[a-zA-Z0-9-_]+=[a-zA-Z0-9-%]+&?)?$/,
      'Enter a valid URL',
    ),
    dealer_code: Yup.string().when([], {
      is: () => isSharingInventory,
      then: (schema) =>
        schema.required(
          'Dealer code is required if you want to sharing inventory',
        ),
      otherwise: (schema) => schema.notRequired(),
    }),
    address: Yup.array().of(
      Yup.object().shape({
        street: Yup.string().required('Street is required'),
        city: Yup.string().required('City is required'),
        province: Yup.string().required('Country is required'),
        postcode: Yup.string().required('Postcode is required'),
        country: Yup.string().required('Please select country'),
      }),
    ),
  })

export const sellerRegistrationProcessBankDetailsSchema = Yup.object().shape({
  seller_bank_name: Yup.string().required('Bank name is required'),
  seller_bank_branch_name: Yup.string().required(
    'Bank branch name is required',
  ),
  seller_account_number: Yup.string()
    .required('Account number is required')
    .oneOf(
      [Yup.ref('confirm_seller_account_number'), null],
      'Account number are not the same',
    ),
  confirm_seller_account_number: Yup.string()
    .required('Account number is required')
    .oneOf(
      [Yup.ref('seller_account_number'), null],
      'Account number are not the same',
    ),
  seller_account_holder_name: Yup.string().required(
    'Account holder name is required',
  ),
  seller_routing_number: Yup.string().required('Routing number is required'),
  seller_bank_address: Yup.string().required('Bank address is required'),
})

export const editProfilePersonalDetailsSchema = Yup.object().shape({
  first_name: Yup.string().required('First name is required'),
  last_name: Yup.string().required('Last name is required'),
  email: Yup.string().email('Invalid email').required('Email is required'),
  phone: Yup.string().required('Phone Number is required'),
  new_password: Yup.string()
    .transform((value) => value.trim())
    .matches(
      /^(?=.*[A-Z])(?=.*[a-z])(?=.*[0-9])(?=.*[\W_]).{8,}$/,
      'Password must contain at least one uppercase letter, one lowercase letter, one digit, one special character, and be at least 8 characters long',
    ),
})

export const sellerEditProfileValidationSchema = (isSharingInventory) =>
  Yup.object().shape({
    name: Yup.string().required('Dealership name is required'),
    phone: Yup.string()
      .required('Phone number is required')
      .matches(
        /^(\d{10}|\(\d{3}\) \d{3}-\d{4})$/,
        'Phone number must be in the format (xxx) xxx-xxxx',
      ),
    contact_person: Yup.string().required('Contact person is required'),
    email: Yup.string().email('Invalid email address'),
    website: Yup.string().matches(
      /((https?):\/\/)?(www.)?[a-z0-9]+(\.[a-z]{2,}){1,3}(#?\/?[a-zA-Z0-9#]+)*\/?(\?[a-zA-Z0-9-_]+=[a-zA-Z0-9-%]+&?)?$/,
      'Enter a valid URL',
    ),
    dealer_code: Yup.string().when([], {
      is: () => isSharingInventory,
      then: (schema) =>
        schema.required(
          'Dealer code is required if you want to sharing inventory',
        ),
      otherwise: (schema) => schema.notRequired(),
    }),
    address: Yup.array().of(
      Yup.object().shape({
        street: Yup.string().required('Street is required'),
        city: Yup.string().required('City is required'),
        province: Yup.string().required('Country is required'),
        postcode: Yup.string().required('Postcode is required'),
        country: Yup.string().required('Please select country'),
      }),
    ),
    hst_number: Yup.string().when('address', (address, schema) => {
      const hasCanada = address?.[0]?.some
        ? address[0].some((addr) => addr.country === 'Canada')
        : false

      return hasCanada
        ? schema.required('HST Number is required for Canadian dealers')
        : schema.notRequired()
    }),
  })

export const buyerEditProfileValidationSchema = (isSharingInventory) =>
  Yup.object().shape({
    name: Yup.string().required('Dealer name is required'),
    phone: Yup.string()
      .required('Phone number is required')
      .matches(
        /^(\d{10}|\(\d{3}\) \d{3}-\d{4})$/,
        'Phone number must be in the format (xxx) xxx-xxxx',
      ),
    contact_person: Yup.string().required('Contact person is required'),
    intrested_province: Yup.array().min(
      1,
      'Please select at least one state/province from where you wish to purchase a vehicle.',
    ),
    email: Yup.string().email('Invalid email address'),
    website: Yup.string().matches(
      /((https?):\/\/)?(www.)?[a-z0-9]+(\.[a-z]{2,}){1,3}(#?\/?[a-zA-Z0-9#]+)*\/?(\?[a-zA-Z0-9-_]+=[a-zA-Z0-9-%]+&?)?$/,
      'Enter a valid URL',
    ),
    dealer_code: Yup.string().when([], {
      is: () => isSharingInventory,
      then: (schema) =>
        schema.required(
          'Dealer code is required if you want to sharing inventory',
        ),
      otherwise: (schema) => schema.notRequired(),
    }),
    address: Yup.array().of(
      Yup.object().shape({
        street: Yup.string().required('Street is required'),
        city: Yup.string().required('City is required'),
        province: Yup.string().required('Country is required'),
        postcode: Yup.string().required('Postcode is required'),
        country: Yup.string().required('Please select country'),
      }),
    ),
  })

export const sellerEditProfileBankValidationSchema = Yup.object().shape({
  seller_bank_name: Yup.string().required('Bank name is required'),
  seller_bank_branch_name: Yup.string().required(
    'Bank branch name is required',
  ),
  seller_account_number: Yup.string().required('Account number is required'),
  seller_account_holder_name: Yup.string().required(
    'Account holder name is required',
  ),
  seller_routing_number: Yup.string().required('Routing number is required'),
  seller_bank_address: Yup.string().required('Bank address is required'),
})

export const buyerRegistrationProcessBankDetailsSchema = Yup.object().shape({
  bank_name: Yup.string().required('Bank name is required'),
  bank_branch_name: Yup.string().required('Bank branch name is required'),
  account_number: Yup.string().required('Account number is required'),
  institution_number: Yup.string().required('Institution number is required'),
  transit_number: Yup.string().required('Transit number is required'),
  bank_address: Yup.string().required('Bank address is required'),
})

export const achBankDetailsSchema = Yup.object().shape({
  bank_name: Yup.string().required('Bank name is required'),
  bank_branch_name: Yup.string().nullable(), // Optional for US
  account_number: Yup.string()
    .required('Account number is required')
    .matches(/^\d+$/, 'Account number must be numeric'),
  transit_number: Yup.string()
    .required('Routing number is required')
    .matches(/^\d{9}$/, 'Routing number must be a valid 9-digit number'),
  bank_address: Yup.string().nullable(),
})

export const paymentContactValidationSchema = Yup.object().shape({
  first_name: Yup.string().trim().required('First name is required'),
  last_name: Yup.string().trim().required('Last name is required'),
  title: Yup.string().trim().required('Job title is required'),
  phone: Yup.string().trim().required('Phone number is required'),
  email: Yup.string()
    .trim()
    .email('Please enter a valid email address')
    .required('Email address is required'),
})
